import type { InternalAxiosRequestConfig } from 'axios';
import { getAccessToken } from '@/shared/auth/tokenProvider';
import { ApiErrorKind, HTTP_HEADERS } from '../constants';
import { ApiError } from '../errors';

const newRequestId = (): string =>
  // randomUUID needs a secure context (https or localhost); fall back for plain-http hosts.
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

/**
 * Request interceptor: correlation id + Bearer token.
 * Public endpoints (sign-up, password reset, OTP) opt out with `{ skipAuth: true }`.
 */
export const attachAuth = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  // Kept across the 401 retry so both attempts share one id in the server logs.
  if (!config.headers.has(HTTP_HEADERS.requestId)) {
    config.headers.set(HTTP_HEADERS.requestId, newRequestId());
  }
  if (config.skipAuth) return config;
  // The 401 handler already set a freshly refreshed token on the retry.
  if (config._retry && config.headers.has(HTTP_HEADERS.authorization)) return config;

  let token: string | null;
  try {
    token = await getAccessToken();
  } catch (cause) {
    throw new ApiError({
      kind: ApiErrorKind.Unauthorized,
      message: 'Could not acquire an access token',
      cause,
    });
  }
  if (token) config.headers.set(HTTP_HEADERS.authorization, `Bearer ${token}`);
  return config;
};
